import { useContentfulImages } from '@snapchat/mw-contentful-client';
import type { Block as BlockType, BlockContentsItem } from '@snapchat/mw-contentful-schema';
import type { ImageSources, WidthType } from '@snapchat/snap-design-system-marketing';
import { Block as BlockSDS } from '@snapchat/snap-design-system-marketing';
import type { FC, ReactElement } from 'react';
import { useContext } from 'react';

import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logError } from '../../helpers/logging';
import { useParseMediaContainer } from '../../hooks/useParseMediaContainer';
import { combineImageSources } from '../../utils/combineImageSources';
import { getContentfulInspectorProps } from '../../utils/contentful/getContentfulInspectorProps';
import {
  renderRichText,
  renderRichTextMultiLineWithMarkings,
} from '../../utils/renderText/renderRichText';
import { Accordion } from '../Accordion';
import { AnimatedAccordion } from '../AnimatedAccordion/AnimatedAccordion';
import { CallToAction } from '../CallToAction';
import { CarouselV3 } from '../CarouselV3';
import { ContentShallow } from '../Content/ContentShallow';
import { FormShallow } from '../Form';
import { LazyGallery } from '../Gallery/LazyGallery';
import { type ImageDataProps, getImageSourceSettings } from '../Image';
import { MosaicShallow } from '../Mosaic/MosaicShallow';
import { RelatedContent } from '../RelatedContent';
import { SlugContext } from '../Slug/SlugContext';
import { Tabs } from '../Tabs';
import type { VideoDataProps } from '../Video/types';
import { BarChartShallow } from '../visualizations/BarChart';
import { ChartToggleWrapper } from '../visualizations/ChartToggle';
import { GeoMapShallow } from '../visualizations/GeoMap';
import { LineChartShallow } from '../visualizations/LineChart';
import { MultiVisualizationShallow } from '../visualizations/MultiVisualization';
import { TableShallow } from '../visualizations/Table';
import type { BlockProps } from './types';

type BlockContentProps = Pick<BlockContentsItem, 'sys' | '__typename'>;

export const BlockContent: FC<BlockContentProps> = (props: BlockContentProps): ReactElement => {
  switch (props.__typename) {
    case 'Gallery': {
      return <LazyGallery {...props} />;
    }

    case 'CarouselV3': {
      return <CarouselV3 {...props} />;
    }

    case 'Tabs': {
      return <Tabs {...props} />;
    }

    case 'Mosaic': {
      return <MosaicShallow {...props} __typename="Mosaic" />;
    }

    case 'Form': {
      return <FormShallow {...props} />;
    }

    case 'ChartToggle': {
      return <ChartToggleWrapper {...props} />;
    }

    case 'BarChart': {
      return <BarChartShallow {...props} />;
    }

    case 'LineChart': {
      return <LineChartShallow {...props} />;
    }

    case 'GeoVisualization': {
      return <GeoMapShallow {...props} />;
    }

    case 'VisualizationSelector': {
      return <MultiVisualizationShallow {...props} />;
    }

    case 'TableVisualization': {
      return <TableShallow {...props} />;
    }

    case 'Content': {
      return <ContentShallow {...props} />;
    }

    case 'Accordion': {
      return <Accordion {...props} />;
    }

    case 'AnimatedAccordion': {
      return <AnimatedAccordion {...props} />;
    }

    case 'RelatedContent': {
      return <RelatedContent {...props} />;
    }

    default: {
      logError({
        component: 'Block',
        message: `Unknown content type: ${props.__typename} with id: ${props.sys.id}`,
      });

      return <>👿</>;
    }
  }
};
BlockContent.displayName = 'BlockContent';

export const Block: FC<BlockProps> = props => {
  const {
    maxColumns,
    eyebrow,
    title,
    subtitle,
    titleAlignment,
    titleAlignmentMobile,
    contentsCollection,
    callsToActionCollection,
    anchorId,
    isNarrow,
    widthStyle,
    fullHeight,
    backgroundColor,
    backgroundMediaV2,
    theme,
    isNextSameBackgroundColor,
    isPreviousSameBackgroundColor,
    className,
    sys,
    preChildren,
    postChildren,
    showCurtain: givenShowCurtain,
  } = props;
  const { replacements } = useContext(SlugContext);
  let finalContentsCollection = contentsCollection;

  if (contentsCollection?.items.length && replacements) {
    const contentsCollectionCopy = [...contentsCollection.items];

    for (let i = 0; i < contentsCollectionCopy.length; i++) {
      const contentId = contentsCollectionCopy[i]?.sys?.id;
      const contentType = contentsCollectionCopy[i]?.__typename;

      if (contentId && replacements && contentId in replacements) {
        const replacement = replacements[contentId];

        if (replacement === undefined) {
          contentsCollectionCopy.splice(i, 1);
        } else if (replacement && replacement.__typename === contentType) {
          contentsCollectionCopy[i] = {
            sys: { id: replacement.sys.id },
            __typename: replacement.__typename,
          };
        }
      }
    }

    finalContentsCollection = { items: contentsCollectionCopy };
  }

  const {
    media: {
      imageSource: backgroundImageSource,
      imageAltText: backgroundImageAltText,
      videoSource: backgroundVideoSource,
    },
    mobileMedia: {
      imageSource: mobileBackgroundImageSource,
      imageAltText: mobileBackgroundImageAltText,
      videoSource: mobileBackgroundVideoSource,
    },
    thumbnailSource,
  } = useParseMediaContainer(backgroundMediaV2 as ImageDataProps | VideoDataProps);

  const blockChildren = (finalContentsCollection?.items ?? []).map(item => (
    <BlockContent key={item.sys.id} {...(item as BlockContentProps)} />
  ));

  const callsToAction = callsToActionCollection.items.map(item => (
    <CallToAction key={item.sys.id} {...item} />
  ));

  const { getImageSources } = useContentfulImages();

  let bgImgSrc: ImageSources | undefined;

  if (backgroundMediaV2?.__typename === 'Image') {
    const { desktopSettings, mobileSettings } = getImageSourceSettings({
      desktopHeight: backgroundMediaV2.media?.height ?? 0,
      mobileHeight: backgroundMediaV2.mobileMedia?.height ?? 0,
      enableHighDpi: backgroundMediaV2.enableHighDpi,
      quality: backgroundMediaV2.quality,
    });

    bgImgSrc = combineImageSources({
      desktop: getImageSources(backgroundImageSource, desktopSettings),
      mobile: getImageSources(mobileBackgroundImageSource, mobileSettings),
    });
  }

  const backgroundMediaStickers = backgroundMediaV2?.stickersCollection?.items?.map(sticker => ({
    imgSrcs: getImageSources(sticker?.asset?.url) as ImageSources,
    position: sticker?.position,
    rotation: sticker?.rotation,
  }));

  const inspectorDatasets = getContentfulInspectorProps<BlockType>({
    entryId: sys.id,
    fieldIds: ['eyebrow', 'title', 'subtitle'],
  });

  // We are checking if showCurtain is different from false
  // because we need to take in consideration the undefined value,
  // if its undefined and there is a background image or video, the curtain should be shown too.
  const showCurtain = (!!bgImgSrc || !!backgroundVideoSource) && givenShowCurtain !== false;

  return (
    // TODO: Think about adding a number for block index when anchorId is not present
    // Relevant thread: https://snap.enterprise.slack.com/archives/G01LFA59Y3U/p1774022344155969
    <BlockSDS
      callsToAction={callsToAction}
      anchorId={anchorId}
      maxColumns={maxColumns ?? undefined}
      subtitle={renderRichText(subtitle)}
      eyebrow={eyebrow}
      title={renderRichTextMultiLineWithMarkings(title)}
      titleAlignment={titleAlignment}
      titleAlignmentMobile={titleAlignmentMobile}
      widthStyle={(widthStyle as WidthType) ?? (isNarrow ? 'narrow' : undefined)}
      backgroundColor={backgroundColor ?? theme}
      isNextSameBackgroundColor={isNextSameBackgroundColor}
      isPreviousSameBackgroundColor={isPreviousSameBackgroundColor}
      fullHeight={fullHeight}
      className={className}
      {...inspectorDatasets}
      preChildren={preChildren}
      postChildren={postChildren}
      backgroundVideoSource={backgroundVideoSource}
      backgroundPosterSource={thumbnailSource}
      mobileBackgroundVideoSource={mobileBackgroundVideoSource}
      backgroundImageSources={bgImgSrc}
      backgroundImageAltText={backgroundImageAltText ?? mobileBackgroundImageAltText}
      showCurtain={showCurtain}
      backgroundMediaStickers={backgroundMediaStickers}
    >
      {anchorId ? (
        <ElementLocation.Provider value={{ elementLocation: `block#${anchorId}` }}>
          {blockChildren}
        </ElementLocation.Provider>
      ) : (
        blockChildren
      )}
    </BlockSDS>
  );
};

Block.displayName = 'Block';
